import { useCallback, useEffect, useState } from "react";
import { api, errMsg, MONTHS_IT, download, pad } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";

const YEARS = Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 4 + i);

const MONTHLY_COLS = [
  ["code", "Codice"], ["last_name", "Cognome"], ["first_name", "Nome"], ["department", "Reparto"],
  ["PRESENTE", "Presenti"], ["ASSENTE", "Assenze"], ["FERIE", "Ferie"], ["MALATTIA", "Malattia"],
  ["INFORTUNIO", "Infortunio"], ["PERMESSO", "Permessi"], ["RIPOSO", "Riposo"],
];

const ACC_COLS = [
  ["last_name", "Cognome"], ["first_name", "Nome"], ["code", "Codice dipendente"],
  ["PRESENTE", "Giorni lavorati"], ["FERIE", "Giorni ferie"], ["MALATTIA", "Giorni malattia"],
  ["INFORTUNIO", "Giorni infortunio"],
  ["PERMESSO", "Giorni permesso"], ["ASSENTE", "Giorni assenza"], ["RIPOSO", "Giorni riposo"],
];

export default function Reports({ kind = "monthly" }) {
  const { isAdmin } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [department, setDepartment] = useState("all");
  const [employeeId, setEmployeeId] = useState("all");
  const [empStatus, setEmpStatus] = useState("active");
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const cols = kind === "accountant" ? ACC_COLS : MONTHLY_COLS;

  useEffect(() => {
    Promise.all([api.get("/departments"), api.get("/employees")])
      .then(([d, e]) => { setDepartments(d.data); setEmployees(e.data); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/monthly", {
        params: { year, month, department, status: empStatus, employee_id: kind === "accountant" ? "all" : employeeId },
      });
      setRows(data.rows);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [year, month, department, employeeId, kind, empStatus]);

  useEffect(() => { load(); }, [load]);

  const totals = cols.filter(([k]) => !["code", "last_name", "first_name", "department"].includes(k))
    .reduce((acc, [k]) => ({ ...acc, [k]: rows.reduce((s, r) => s + Number(r[k] || 0), 0) }), {});

  const exp = (fmt) => {
    const qs = `year=${year}&month=${month}&fmt=${fmt}&department=${department}&status=${empStatus}&employee_id=${kind === "accountant" ? "all" : employeeId}&kind=${kind}`;
    const name = kind === "accountant" ? "report_commercialista" : "report_mensile";
    download(`/export/report?${qs}`, `${name}_${year}_${pad(month)}.${fmt}`)
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div data-testid={kind === "accountant" ? "accountant-page" : "reports-page"} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">
            {kind === "accountant" ? "Report Commercialista" : "Report Mensile"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 tabular-nums">{MONTHS_IT[month - 1]} {year} · {rows.length} dipendenti</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} variant={f === "xlsx" ? "default" : "outline"} size="sm" className="rounded-sm font-semibold"
                onClick={() => exp(f)} data-testid={`export-report-${f}`}>
                <DownloadSimple size={14} className="mr-1.5" /> {f === "xlsx" ? "EXCEL" : f.toUpperCase()}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap gap-2">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-[140px] rounded-sm" data-testid="report-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[100px] rounded-sm" data-testid="report-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="report-department"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i reparti</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={empStatus} onValueChange={setEmpStatus}>
          <SelectTrigger className="h-9 w-[190px] rounded-sm" data-testid="report-emp-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Solo dipendenti attivi</SelectItem>
            <SelectItem value="inactive">Solo non attivi</SelectItem>
            <SelectItem value="all">Attivi e non attivi</SelectItem>
          </SelectContent>
        </Select>
        {kind !== "accountant" && (
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="h-9 w-[230px] rounded-sm" data-testid="report-employee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutti i dipendenti</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm tbl-sticky" data-testid="report-table">
          <thead className="bg-secondary">
            <tr>
              {cols.map(([k, l]) => (
                <th key={k} className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap
                  ${["code", "last_name", "first_name", "department"].includes(k) ? "text-left" : "text-right"}`}>{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-muted-foreground" data-testid="report-empty">Nessun dato</td></tr>}
            {rows.map((r) => (
              <tr key={r.employee_id} className="border-t border-border hover:bg-secondary/40" data-testid={`report-row-${r.code}`}>
                {cols.map(([k]) => (
                  <td key={k} className={`px-3 py-1.5 whitespace-nowrap ${["code", "last_name", "first_name", "department"].includes(k)
                    ? "text-left font-medium" : "text-right tabular-nums"}`}>
                    {r[k] ?? 0}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-secondary font-semibold">
              <tr data-testid="report-totals">
                {cols.map(([k], i) => (
                  <td key={k} className={`px-3 py-2 ${["code", "last_name", "first_name", "department"].includes(k) ? "text-left" : "text-right tabular-nums"}`}>
                    {i === 0 ? "TOTALE" : (totals[k] !== undefined ? Math.round(totals[k] * 100) / 100 : "")}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {!isAdmin && <p className="text-xs text-muted-foreground">L'esportazione dei report è riservata agli amministratori.</p>}
    </div>
  );
}
